<script lang="ts" setup>
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useDeleteFirmware } from "~/modules/firmware/composables/useDeleteFirmware";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

const props = defineProps<{
  businessUnitCode: number;
  firmware: Firmware;
}>();
const emit = defineEmits(["change"]);

const { t } = useI18n();

const [deleteConfirmDialogVisible, toggleDeleteConfirmVisible] = useToggle();

const { isLoading, error, deleteFirmware } = useDeleteFirmware({
  businessUnitCode: props.businessUnitCode,
});

const onDeleteFirmwareAndPropagateChange = async () => {
  toggleDeleteConfirmVisible(false);

  await deleteFirmware(props.firmware.uuid);

  if (error.value) {
    error.value.notify(NotificationKey.saveError);
  } else {
    showNotificationSuccess(t(NotificationKey.removeSuccess));
    emit("change");
  }
};

const onDeleteButton = async (event: MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
  toggleDeleteConfirmVisible(true);
};
</script>

<template>
  <lxc-button
    html-type="button"
    type="borderless"
    :disabled="isLoading"
    :icon="!isLoading ? ILxcLightTrash2 : undefined"
    :title="t('firmware.actions.delete.label')"
    class="invisible"
    @click="onDeleteButton"
  >
    <lxc-loader v-if="isLoading" />
  </lxc-button>

  <confirm-modal
    :is-dialog-visible="deleteConfirmDialogVisible"
    :title="t('firmware.actions.delete.label')"
    :description="
      t('firmware.actions.delete.description', { name: firmware.name })
    "
    :icon="ILxcLightTrash2"
    icon-color-theme="error"
    color-theme="danger"
    @cancel="toggleDeleteConfirmVisible"
    @confirm="onDeleteFirmwareAndPropagateChange"
  />
</template>
<style lang="scss" scoped>
button {
  :deep(svg) {
    height: 20px;
    width: 20px;
  }
}
</style>
